import { Field as ChakraField, Input, Text, VStack } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useDeleteOrganizationMutation } from '@services/config/organization';
import Yup from '@utils/validation/yup-extended';
import { Field, Formik } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Organization } from '@/types/config/config.types';

type Props = {
  selectedOrganization: Organization | null;
  setSelectedOrganization: Dispatch<SetStateAction<Organization | null>>;
};

export default function DeleteOrganizationModal({ selectedOrganization, setSelectedOrganization }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [deleteOrganization, { isLoading }] = useDeleteOrganizationMutation();

  return (
    <Formik
      initialValues={{
        organizationName: '',
      }}
      onSubmit={() => {
        if (!selectedOrganization) {
          return;
        }
        deleteOrganization(selectedOrganization.id)
          .unwrap()
          .then(() => {
            createToast(
              formatMessage({ id: 'components.organizations.message.delete' }, { name: selectedOrganization.name }),
              ToastStatusEnum.SUCCESS,
            );
            setSelectedOrganization(null);
          });
      }}
      validationSchema={
        selectedOrganization &&
        Yup.object({
          organizationName: Yup.string().required().oneOf([selectedOrganization.name.toUpperCase()]),
        })
      }
      validateOnMount
    >
      {({ errors, touched, isValid, handleSubmit }) => {
        const confirmationName = selectedOrganization?.name.toUpperCase();
        return (
          <CockpitDialog
            open={selectedOrganization !== null}
            isLoading={isLoading}
            isDisabled={!isValid}
            onCancel={() => setSelectedOrganization(null)}
            onConfirm={handleSubmit}
            otherChildren={
              <VStack width="100%" gap={2} padding={3} border="2px solid" borderColor="neutral.800">
                <Text color="neutral.300" width="100%">
                  <FormattedMessage
                    id="components.organizations.popup.descriptionDeleteConfirmation"
                    values={{ name: confirmationName }}
                  />
                </Text>
                <ChakraField.Root
                  variant="cockpit"
                  required
                  invalid={touched.organizationName && !!errors.organizationName}
                >
                  <Field
                    as={Input}
                    placeholder={confirmationName}
                    type="text"
                    name="organizationName"
                    id="organizationName"
                  />
                </ChakraField.Root>
              </VStack>
            }
          >
            <Text color="neutral.100" width="100%" whiteSpace="pre-line">
              <FormattedMessage
                id="components.organizations.popup.descriptionDelete"
                values={{ name: selectedOrganization?.name }}
              />
            </Text>
            <Text color="neutral.100" width="100%" whiteSpace="pre-line">
              <FormattedMessage id="components.organizations.popup.descriptionDeleteWarning" />
            </Text>
          </CockpitDialog>
        );
      }}
    </Formik>
  );
}
